import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Mail, BookOpen, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { API } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center animate-fade-in-up">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Check Your Email</h2>
            <p className="text-gray-400">We've sent password reset instructions to {email}</p>
          </div>

          <Card className="glass-card animate-slide-in-right border-none">
            <CardContent className="p-6 text-center">
              <div className="space-y-4">
                <div className="text-sm text-gray-300">
                  <p>Click the link in the email to reset your password.</p>
                  <p>The link will expire in 1 hour.</p>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-500 mb-3">Didn't receive an email?</p>
                  <p className="text-xs text-gray-600 mb-3">Check your spam/junk folder first</p>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                    }}
                    variant="outline"
                    size="sm"
                    className="border-purple-500/30 hover:bg-purple-500/10 text-purple-300"
                  >
                    Try Again
                  </Button>
                </div>

                <div className="text-center">
                  <Link to="/login" className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors">
                    <ArrowLeft className="w-4 h-4 inline mr-1" />
                    Back to Login
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center animate-fade-in-up">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold gradient-text font-fredoka">Forgot Password</h2>
          <p className="text-gray-400 mt-2">Enter your email to reset your password</p>
        </div>

        {/* Form */}
        <Card className="glass-card animate-slide-in-right border-none">
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-center space-x-2 text-white">
              <Mail className="w-5 h-5 text-purple-400" />
              <span>Reset Password</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="bg-red-900/20 border-red-500/50 text-red-200">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="form-input bg-black/20 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500">
                  We'll send you a link to reset your password
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Help */}
        <Card className="glass-card animate-slide-in-right border-none" style={{ animationDelay: '0.1s' }}>
          <CardContent className="p-4">
            <div className="text-center">
              <h3 className="font-semibold text-gray-200 text-sm mb-2">Need Help?</h3>
              <p className="text-xs text-gray-400">
                If you don't receive an email, check your spam folder or contact support.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;